import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useQueryClient } from '@tanstack/react-query';
import CompetitorCircuitSurveyCompleted from '../../mymission/CompetitorCircuitSurveyCompleted';
import { getCircuitSurveyContext } from '../../../helpers/competitorCircuitSurvey';
import * as DocumentPicker from 'expo-document-picker';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CancelToken } from 'axios';
import { Image, Platform, Pressable, Text, useWindowDimensions, View } from 'react-native';
import { HexiaSurveyNative, type NativeSurveyPresentation, type NativeSurveyTheme } from '@hexia/survey-client-native';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import type { CompletionResult, JsonObject, StoredFile, SurveyHostAdapter, SurveyRuntime, SurveyRuntimeState } from '@hexia/survey-client/core';
import { SurveyDateTimeInput } from './SurveyDateTimeInput';
import { SurveyImagePreview } from './SurveyImagePreview';
import { getDevelopmentApiHeaders } from '../../../api/developmentHeaders';
import { LinearGradient } from 'expo-linear-gradient';
import {
  GetSurveyFromMissionAssignationViewModel,
  MyMissionClient,
  PartialSaveMissionAnswerRequest,
  SaveMissionAnswerDto,
  SurveyFormModel,
  SurveyImagesClient,
} from '../../../api/api.g';
import useApi from '../../../hooks/api-hook';
import { useAppDispatch, useAppSelector } from '../../../hooks/store-hook';
import { StringHelper } from '../../../helpers/stringHelper';
import { missionsActions } from '../../../store/missionsSlice';
import { navBarActions } from '../../../store/navBarSlice';
import Loading from '../Loading';
import CustomButton from '../CustomButton';
import Label from '../Label';
import useCustomTheme from '../../../hooks/theme-hook';
import useTranslation from '../../../hooks/translation-hook';
import {
  cloneSurveyData,
  getJsonDiffShallow,
  getPersistableSurveyData,
  SurveySaveCoordinator,
} from '../survey-js/surveySaveCoordinator';
import { submitSurveyWithDeadline, writeSurvey } from '../survey-js/surveyWrite';
import { getSurveySubmissionErrorKey } from './surveySubmissionFailure';
import { canShowSurveySideNavigation, getSurveySideNavigationOffset } from '../survey-js/surveyLayout';
import { clearSurveyDrafts, saveSurveyDraftSnapshot } from './surveyDraftCache';
import { withReceiptAmountConstraints } from './receiptAmountConstraints';
import { prepareSurveyUpload } from './surveyUpload';

type Props = {
  route: { name?: string; params?: { missionAssignationId?: string } };
  survey: GetSurveyFromMissionAssignationViewModel;
  parsedForms: JsonObject[];
};

type Snapshot = {
  formIndex: number;
  data: JsonObject;
  currentPageIndex: number;
};

const surveyPlatform = Platform.OS === 'web'
  ? 'web'
  : Platform.OS === 'ios'
    ? 'ios'
    : 'android';

const formChain = (first?: SurveyFormModel): SurveyFormModel[] => {
  const result: SurveyFormModel[] = [];
  let current = first;
  while (current?.formVersionId) {
    result.push(current);
    current = current.nextForm;
  }
  return result;
};

const parseObject = (json?: string): JsonObject => {
  try {
    const value = JSON.parse(json ?? '{}');
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  } catch {
    return {};
  }
};

const parseMetadata = (json?: string): Record<string, unknown> => {
  try {
    return JSON.parse(json ?? '{}') as Record<string, unknown>;
  } catch {
    return {};
  }
};

const themeColor = (value: unknown, fallback: string): string => typeof value === 'string' ? value : fallback;

const HexiaNativeSurvey = ({ route, survey: bootstrap, parsedForms }: Props) => {
  const missionAssignationId = route.params?.missionAssignationId;
  const forms = useMemo(() => formChain(bootstrap.survey), [bootstrap.survey]);
  const initialAnswers = useMemo(() => forms.map((form) => parseObject(form.formAnswerJson)), [forms]);
  const metadata = useMemo(() => forms.map((form) => parseMetadata(form.metadataJson)), [forms]);
  const restoredIndex = Math.max(0, forms.findIndex((form) => form.formVersionId === metadata[0]?.currentMission));
  const [formIndex, setFormIndex] = useState(restoredIndex);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const queryClient = useQueryClient();
  const [error, setError] = useState<string>();
  const [previewFile, setPreviewFile] = useState<StoredFile>();
  const presentation = useMemo<NativeSurveyPresentation>(() => ({
    renderDateTimeInput: (props) => <SurveyDateTimeInput {...props} />,
    onPreviewFile: setPreviewFile,
  }), []);
  const [runtimeState, setRuntimeState] = useState<SurveyRuntimeState>();
  const runtimeRef = useRef<SurveyRuntime | undefined>(undefined);
  const dimension = useWindowDimensions();
  const apiMissions = useApi(MyMissionClient);
  const apiImages = useApi(SurveyImagesClient);
  const dispatch = useAppDispatch();
  const navigation = useNavigation();
  const userPrefs = useAppSelector((selector) => selector.userPrefs);
  const theme = useCustomTheme();
  const surveyTheme = useMemo<NativeSurveyTheme>(() => theme.isDark ? {
    background: themeColor(theme.background?.primary, '#2b3a46'),
    surface: themeColor(theme.background?.primaryLight, '#344450'),
    surfaceMuted: themeColor(theme.background?.modal, '#465866'),
    border: themeColor(theme.borderColorLight, '#5f7486'),
    borderStrong: themeColor(theme.borderColor, '#607383'),
    text: themeColor(theme.text?.primary, '#ffffff'),
    textMuted: '#c8d2d9',
    primary: themeColor(theme.text?.darkTurquoiseTwo, '#5fc8ba'),
    primaryText: themeColor(theme.text?.inverse, '#102a30'),
    primaryMuted: themeColor(theme.background?.lightSea, '#3f4f5d'),
    danger: themeColor(theme.text?.error, '#ff9bb4'),
    shadow: '#000000',
  } : {}, [theme]);
  const t = useTranslation();
  const french = (bootstrap.restrictedLanguage ?? userPrefs.uiLanguage ?? '').toLowerCase().startsWith('fr');
  const receiptAmountError = t('survey-receipt-amount-invalid', { defaultValue: french ? 'Saisissez un montant positif ou nul avec au plus deux décimales.' : 'Enter a non-negative amount with at most two decimal places.' });
  const constrainedForms = useMemo(() => parsedForms.map((form) => withReceiptAmountConstraints(form, receiptAmountError)), [parsedForms, receiptAmountError]);
  const draftsRef = useRef(initialAnswers.map((answer) => cloneSurveyData(answer)));
  const savedRef = useRef(initialAnswers.map((answer) => cloneSurveyData(answer)));
  const statesRef = useRef<Array<SurveyRuntimeState | undefined>>([]);
  const stateSignaturesRef = useRef(initialAnswers.map((answer, index) => JSON.stringify({
    data: answer,
    page: Number(metadata[index]?.currentPageNo ?? 0),
  })));
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const coordinatorRef = useRef(new SurveySaveCoordinator());
  const mountedRef = useRef(true);
  const submittingRef = useRef(false);

  useFocusEffect(useCallback(() => {
    dispatch(navBarActions.setVisibility(false));
  }, [dispatch]));

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const updateDraft = (snapshot: Snapshot) => {
    draftsRef.current[snapshot.formIndex] = getPersistableSurveyData(
      snapshot.data,
      savedRef.current[snapshot.formIndex] ?? {},
    );
  };

  const buildPartialRequests = (snapshot: Snapshot) => forms.map((form, index) => {
    const nextMetadata = { ...metadata[index] };
    if (index === snapshot.formIndex) nextMetadata.currentPageNo = snapshot.currentPageIndex;
    if (index === 0) nextMetadata.currentMission = forms[snapshot.formIndex]?.formVersionId;

    return PartialSaveMissionAnswerRequest.fromJS({
      formVersionId: form.formVersionId,
      metadataJson: JSON.stringify(nextMetadata),
      answer: {
        answerJson: JSON.stringify(getJsonDiffShallow(draftsRef.current[index] ?? {}, savedRef.current[index] ?? {})),
        missionAssignationId: form.missionAssignationId,
        periodMissionConfigurationId: form.periodMissionConfigurationId,
        rootMissionAssignationId: missionAssignationId,
      },
    });
  });

  const persist = async (snapshot: Snapshot, token?: CancelToken): Promise<boolean> => {
    if (!missionAssignationId) return false;
    updateDraft(snapshot);
    const draftsAtRequest = draftsRef.current.map((answer) => cloneSurveyData(answer));
    const save = async (requestToken: CancelToken) => {
      const saved = await apiMissions.save(
        missionAssignationId,
        new Date().getTimezoneOffset(),
        buildPartialRequests(snapshot),
        requestToken,
      );
      requestToken.throwIfRequested();
      if (saved) savedRef.current = draftsAtRequest;
      return saved;
    };
    return token ? save(token) : writeSurvey(save);
  };

  const enqueueSave = (snapshot: Snapshot) => coordinatorRef.current.enqueue(() => persist(snapshot));

  const queueSave = (snapshot: Snapshot) => {
    updateDraft(snapshot);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (route.name !== 'survey') return;
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = undefined;
      void enqueueSave(snapshot).catch(() => undefined);
    }, 1500);
  };

  const uploadFiles: SurveyHostAdapter['uploadFiles'] = async ({ files }) => {
    const results = [];
    const preparedFiles = files.map((candidate) => prepareSurveyUpload(candidate, Platform.OS === 'web'));
    for (const file of preparedFiles) {
      const formData = new FormData();
      formData.append('file', file.body as Blob);
      const baseUrl = (apiImages as any).baseUrl ?? '';
      const options = await (apiImages as any).transformOptions({
        method: 'POST',
        url: `${baseUrl}/api/ms/client/survey-images`,
        data: formData,
        headers: { Accept: 'application/json' },
        cancelToken: apiImages.tokenSource?.token,
        timeout: 60000,
      });
      const response = await (apiImages as any).instance.request(options);
      const content = response.data?.content ?? response.data?.Content ?? response.data?.url ?? response.data?.Url;
      if (typeof content !== 'string' || !/^https?:\/\//i.test(content)) throw new Error('Upload did not return a stored file reference.');
      results.push({
        name: file.name,
        type: file.type,
        content,
      });
    }
    return results;
  };

  const host = useMemo<SurveyHostAdapter>(() => ({
    uploadFiles,
    pickFiles: async ({ allowMultiple, acceptedTypes }) => {
      const result = await DocumentPicker.getDocumentAsync({
        multiple: allowMultiple,
        type: acceptedTypes ? acceptedTypes.split(',').map((value) => value.trim()) : ['image/png', 'image/jpeg', 'application/pdf'],
        copyToCacheDirectory: true,
      });
      return result.canceled ? [] : result.assets;
    },
    deleteFiles: async ({ files }) => {
      const activeForm = forms[formIndex];
      if (!activeForm?.missionAssignationId) return;
      await Promise.all(files.map((file) => {
        const imageId = file.content?.match(/\/api\/ms\/client\/survey-images\/([0-9a-f-]{36})(?:$|\?)/i)?.[1];
        return imageId
          ? apiImages.delete(imageId, activeForm.missionAssignationId, undefined, apiImages.tokenSource?.token)
          : Promise.resolve(undefined);
      }));
    },
    loadRemoteChoices: async ({ configuration }) => {
      if (typeof configuration.url !== 'string') return [];
      const activeData = draftsRef.current[formIndex] ?? {};
      const url = configuration.url.replace(/\{([^}]+)\}/g, (_match, name: string) => encodeURIComponent(String(activeData[name.trim()] ?? '')));
      const token = await apiMissions.config.accessToken();
      const response = await fetch(url, { headers: { ...getDevelopmentApiHeaders(url), ...(token ? { Authorization: `Firebase ${token}` } : {}) } });
      if (!response.ok) throw new Error(`Remote choices request failed with ${response.status}.`);
      let payload: unknown = await response.json();
      if (typeof configuration.path === 'string') {
        payload = configuration.path.split('.').filter(Boolean).reduce<unknown>((value, part) => value && typeof value === 'object' ? (value as Record<string, unknown>)[part] : undefined, payload);
      }
      const values = Array.isArray(payload) ? payload : [];
      const valueName = typeof configuration.valueName === 'string' ? configuration.valueName : 'value';
      const titleName = typeof configuration.titleName === 'string' ? configuration.titleName : 'text';
      return values.map((item) => {
        const record = item && typeof item === 'object' ? item as Record<string, unknown> : undefined;
        const value = (record?.[valueName] ?? item) as string | number | boolean | null;
        return { value, text: String(record?.[titleName] ?? value ?? ''), raw: item as any };
      });
    },
  }), [apiImages, apiMissions, formIndex, forms]);

  const onStateChange = (state: SurveyRuntimeState) => {
    setRuntimeState(state);
    statesRef.current[formIndex] = state;
    const signature = JSON.stringify({ data: state.data, page: state.currentPageIndex });
    if (stateSignaturesRef.current[formIndex] === signature) return;
    stateSignaturesRef.current[formIndex] = signature;
    const activeFormVersionId = forms[formIndex]?.formVersionId;
    if (missionAssignationId && activeFormVersionId) {
      void saveSurveyDraftSnapshot(missionAssignationId, activeFormVersionId, {
        data: state.data,
        currentPageIndex: state.currentPageIndex,
      });
    }
    queueSave({ formIndex, data: state.data, currentPageIndex: state.currentPageIndex });
  };

  const submitFinal = async (snapshot: Snapshot) => {
    if (!missionAssignationId || submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    setError(undefined);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    updateDraft(snapshot);

    try {
      await submitSurveyWithDeadline(
        coordinatorRef.current,
        (token) => persist(snapshot, token),
        (token) => apiMissions.saveAndComplete(
          missionAssignationId,
          new Date().getTimezoneOffset(),
          forms.map((form, index) => SaveMissionAnswerDto.fromJS({
            missionAssignationId: form.missionAssignationId,
            periodMissionConfigurationId: form.periodMissionConfigurationId,
            rootMissionAssignationId: missionAssignationId,
            answerJson: JSON.stringify(getJsonDiffShallow(draftsRef.current[index] ?? {}, savedRef.current[index] ?? {})),
          })),
          token,
        ),
      );
      // Local cleanup is best-effort and must not turn a confirmed submission
      // into an error or keep the progress overlay open.
      void clearSurveyDrafts(missionAssignationId).catch(() => undefined);
      if (!mountedRef.current) return;
      const circuitContext = getCircuitSurveyContext(bootstrap);
      dispatch(missionsActions.setMissionAcceptedId(circuitContext?.mainMissionAssignationId ?? missionAssignationId));
      dispatch(missionsActions.setReloadMissionListSeed(StringHelper.generateRandomString()));
      if (circuitContext) {
        void queryClient.invalidateQueries({ queryKey: ['competitor-circuit', circuitContext.circuitId] });
        setCompleted(true);
      } else {
        navigation.navigate('map' as never);
      }
    } catch (reason) {
      if (mountedRef.current) setError(t(getSurveySubmissionErrorKey(reason)));
    } finally {
      submittingRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
  };

  const onComplete = async (result: CompletionResult) => {
    const state = statesRef.current[formIndex];
    const snapshot = {
      formIndex,
      data: result.data,
      currentPageIndex: state?.currentPageIndex ?? 0,
    };
    if (formIndex < forms.length - 1) {
      setLoading(true);
      try {
        await enqueueSave(snapshot);
        setFormIndex((current) => current + 1);
      } catch (reason) {
        setError(t(getSurveySubmissionErrorKey(reason)));
      } finally {
        setLoading(false);
      }
      return;
    }
    await submitFinal(snapshot);
  };

  const circuitContext = getCircuitSurveyContext(bootstrap);
  const handleRuntimeReady = useCallback((runtime?: SurveyRuntime) => { runtimeRef.current = runtime; }, []);
  if (completed && circuitContext) return <CompetitorCircuitSurveyCompleted context={circuitContext} />;

  const form = forms[formIndex];
  if (!form || !parsedForms[formIndex]) return <Loading />;

  const showSideNavigation = canShowSurveySideNavigation(dimension.width);
  const sideNavigationOffset = getSurveySideNavigationOffset(dimension.width);
  const isFirstStep = formIndex === 0 && (runtimeState?.currentPageIndex ?? 0) === 0;
  const isLastStep = formIndex === forms.length - 1
    && !!runtimeState
    && runtimeState.currentPageIndex >= runtimeState.visiblePages.length - 1;
  const goPrevious = () => {
    if (loading) return;
    if ((runtimeState?.currentPageIndex ?? 0) > 0) runtimeRef.current?.previous();
    else if (formIndex > 0) setFormIndex((current) => current - 1);
  };
  const goNext = () => {
    if (!runtimeRef.current || loading) return;
    if (runtimeState && runtimeState.currentPageIndex >= runtimeState.visiblePages.length - 1) void runtimeRef.current.complete();
    else runtimeRef.current.next();
  };
  const previousControl = (
    <Pressable disabled={loading} style={({ pressed }) => ({ alignItems: 'center', opacity: loading ? 0.45 : pressed ? 0.82 : 1, transform: [{ scale: pressed ? 0.94 : 1 }] })} onPress={goPrevious} accessibilityRole="button" accessibilityLabel={t('previous')} accessibilityState={{ disabled: loading, busy: loading }}>
      <Image style={{ height: 60, width: 60 }} source={require('../../../../assets/previous-button.png')} />
      {showSideNavigation && <Label style={{ color: theme.text.darkTurquoiseTwo, textTransform: 'uppercase', marginTop: 4 }}>{t('previous')}</Label>}
    </Pressable>
  );
  const nextControl = (
    <Pressable disabled={loading} style={({ pressed }) => ({ alignItems: 'center', opacity: loading ? 0.45 : pressed ? 0.82 : 1, transform: [{ scale: pressed ? 0.94 : 1 }] })} onPress={goNext} accessibilityRole="button" accessibilityLabel={t(isLastStep ? 'complete' : 'next')} accessibilityState={{ disabled: loading, busy: loading }}>
      <Image style={{ height: 60, width: 60 }} source={require('../../../../assets/next-button.png')} />
      {showSideNavigation && <Label style={{ color: theme.text.darkTurquoiseTwo, textTransform: 'uppercase', marginTop: 4 }}>{t(isLastStep ? 'complete' : 'next')}</Label>}
    </Pressable>
  );

  return (
    <View style={{ flex: 1, backgroundColor: theme.background.primary as string }}>
      <SurveyImagePreview file={previewFile} onClose={() => setPreviewFile(undefined)} />
      {loading && (
        <Loading
          accessibilityRole="progressbar"
          accessibilityLabel={isLastStep ? t('survey-submitting', { defaultValue: french ? 'Envoi du questionnaire en cours' : 'Submitting survey' }) : t('loading')}
          text={isLastStep ? t('survey-submitting', { defaultValue: french ? 'Envoi du questionnaire en cours...' : 'Submitting survey...' }) : undefined}
          style={{
            zIndex: 2000,
            elevation: 20,
            backgroundColor: theme.isDark ? 'rgba(24, 25, 26, 0.88)' : 'rgba(255, 255, 255, 0.92)',
          }}
        />
      )}
      <View style={{
        paddingHorizontal: 24,
        paddingVertical: 16,
        borderBottomWidth: 1,
        borderBottomColor: theme.borderColorLight as string,
        flexDirection: 'row',
        alignItems: 'center',
      }}>
        {bootstrap.branchInfo?.logoUrl
          ? <Image source={{ uri: bootstrap.branchInfo.logoUrl }} style={{ width: 52, height: 52, borderRadius: 26, marginRight: 16 }} />
          : null}
        <View style={{ flex: 1 }}>
          <Text style={{ color: theme.text.primary as string, fontSize: 20, fontWeight: '700' }}>{bootstrap.subscriptionInfo?.name}</Text>
          <Text style={{ color: (theme.text?.titleDark ?? theme.text?.primary ?? '#000000') as string }}>{bootstrap.branchInfo?.address}</Text>
        </View>
        <Pressable disabled={loading} onPress={() => navigation.navigate('mymissions' as never)} accessibilityRole="button" accessibilityState={{ disabled: loading }}>
          <FontAwesomeIcon icon={faXmark} color={(theme.text?.primary ?? '#000000') as string} size={24} />
        </Pressable>
      </View>
      {error && (
        <View accessibilityRole="alert" style={{ padding: 16, backgroundColor: '#7f1d1d' }}>
          <Text style={{ color: '#ffffff' }}>{error}</Text>
          <CustomButton title={t('try-again')} onPress={() => {
            const state = statesRef.current[formIndex];
            if (state) void onComplete({ data: state.data } as CompletionResult);
          }} />
          <CustomButton secondary title={t('my_missions')} onPress={() => navigation.navigate('mymissions' as never)} />
        </View>
      )}
      <HexiaSurveyNative
        presentation={presentation}
        theme={surveyTheme}
        key={form.formVersionId}
        formJson={constrainedForms[formIndex]}
        initialData={draftsRef.current[formIndex] ?? {}}
        locale={bootstrap.restrictedLanguage ?? userPrefs.uiLanguage}
        platform={surveyPlatform}
        host={host}
        questionsOnPageMode="questionPerPage"
        initialPageIndex={Number(metadata[formIndex]?.currentPageNo ?? 0)}
        contentMaxWidth={640}
        showNavigation={false}
        labels={{
          previous: t('previous'),
          next: t('next'),
          complete: t('complete'),
          addFile: t('choose-file'),
          clear: t('remove'),
          dropFiles: t('survey-file-drop-hint', { defaultValue: french ? 'Glissez-déposez vos fichiers ici ou choisissez un fichier' : 'Drag and drop files here, or choose a file' }),
          fileBusy: t('survey-file-busy', { defaultValue: french ? 'Veuillez attendre la fin du transfert.' : 'Please wait for the file operation to finish.' }),
          fileError: t('survey-file-error', { defaultValue: french ? 'Impossible de mettre à jour les fichiers. Utilisez une image ou un PDF de 10 Mo maximum, puis réessayez.' : 'Unable to update files. Use an image or PDF up to 10 MB, then try again.' }),
          fileInvalid: t('survey-file-invalid-selection', { defaultValue: french ? 'Vérifiez le type, la taille et le nombre de fichiers sélectionnés.' : 'Check the selected file type, size and number of files.' }),
        }}
        onRuntimeReady={handleRuntimeReady}
        onStateChange={onStateChange}
        onComplete={onComplete}
      />
      {showSideNavigation && !isFirstStep && (
        <View style={{ position: 'absolute', zIndex: 1000, left: sideNavigationOffset, top: 160, width: 80 }}>
          {previousControl}
        </View>
      )}
      {showSideNavigation && (
        <View style={{ position: 'absolute', zIndex: 1000, right: sideNavigationOffset, top: 160, width: 80 }}>
          {nextControl}
        </View>
      )}
      {!showSideNavigation && (
        <LinearGradient
          colors={['#024252', '#035164', '#43A49E']}
          start={{ x: 0, y: -0.5 }}
          end={{ x: 1, y: 1 }}
          style={{ alignItems: 'center', justifyContent: 'center', width: '100%', paddingHorizontal: dimension.width >= 600 ? 40 : 24, paddingVertical: 10 }}
        >
          <View style={{ flexDirection: 'row', gap: 40 }}>
            <View style={{ width: 60 }}>{!isFirstStep && previousControl}</View>
            {nextControl}
          </View>
        </LinearGradient>
      )}
    </View>
  );
};

export default HexiaNativeSurvey;
